/**
 * Created by rathompson on 10/13/23
 * File: MoveTenantPreviewContent.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import { useCallback, useContext, useEffect, useState, VFC } from 'react'
import type { Tsg } from '@sparky/framework'
import { Body, Button, ModalBody, ModalFooter, useIsMounted } from '@panwds/react-ui'
import { BUTTON_BACK, BUTTON_SUBMIT } from '../../../common/constants/LabelConstants'
import GlobalContext from '../../../common/context/GlobalContext'
import StringTemplate from '../../../common/utils/StringTemplate'
import {
  LABEL_ARE_YOU_SURE,
  LABEL_TENANT_MOVED_USERS_LOST,
  LABEL_TENANT_WILL_BE_MOVED
} from '../../constants/LabelConstants'
import MoveTenantPreviewTable from './MoveTenantPreviewTable'
import IAMService from '../../../iam/services/IAMService'
import { useMessage } from '../../../common/providers/MessageProvider'
import type { IAM_AccessPolicy } from '../../../common/reducers/types/IAMReducer.types'
import _ from 'lodash'
import { prn } from '@sparky/framework'
import { safeSet } from '../../../common/utils/Utils'
import ScreenLoader from '../../../common/components/screenLoader/ScreenLoader'

const MoveTenantPreviewContent: VFC<{
  tsg: Tsg,
  selected: Tsg,
  onBack: any,
  onSubmit: any
}> = ({ tsg, selected, onBack, onSubmit: _onSubmit }) => {

  const { treeReducer } = useContext(GlobalContext)
  const { sgKeyBy: tsgMap } = treeReducer
  const { createBanner } = useMessage()
  const isMounted = useIsMounted()

  const [loading, setLoading] = useState<boolean>(false)
  const [lostPolicies, setLostPolicies] = useState<IAM_AccessPolicy[]>([])

  const parent = tsgMap[tsg.parent_id as string]

  const loadPolicyData = useCallback(async (tsg, parent) => {
    try {
      safeSet(setLoading, true, isMounted.current)
      const tsgPolicies: IAM_AccessPolicy[] = await IAMService.accessPolicies.list(tsg.id).then(({ data }) => data.items)
      const tsgPolicyMap = _.groupBy(tsgPolicies, 'principal')
      const parentPolicies = await IAMService.accessPolicies.list(parent.id).then(({ data }) => data.items)
      const parentPolicyMap = _.groupBy(parentPolicies, 'principal')

      const policiesUsingInheritedCustomRoles = tsgPolicies.filter(policy => {
        const isCustom = `${policy.role}`.includes(':')
        if (isCustom) {
          const [, tsg_id] = policy.role.split(':')
          return tsg_id !== tsg.id
        }
        return false
      })
      const lostPolicies: IAM_AccessPolicy[] = []
      lostPolicies.push(...policiesUsingInheritedCustomRoles)
      for (const [principal, policies] of Object.entries(tsgPolicyMap)) {
        const userPoliciesFromParent = _.get(parentPolicyMap, principal, [])
        for (const policy of policies) {
          if (policy.inherited_from !== tsg.id) {
            const keepsAccess = userPoliciesFromParent.some((parentPolicy: IAM_AccessPolicy) => {
              const samePrincipal = parentPolicy.principal === policy.principal
              const sameRole = parentPolicy.role === policy.role

              const parentResourceData: any = prn.parse(parentPolicy.resource)
              const resourceData: any = prn.parse(policy.resource)

              const sameApp = parentResourceData.app_id === resourceData.app_id
              const sameRegion = parentResourceData.region_id === resourceData.region_id
              const sameInstance = parentResourceData.instance_id === resourceData.instance_id
              const sameScope = parentResourceData.resource_scope === resourceData.resource_scope

              return samePrincipal && sameRole && sameApp && sameRegion && sameInstance && sameScope
            })
            if (!keepsAccess) {
              lostPolicies.push(policy)
            }
          }
        }
        safeSet(setLostPolicies, [...lostPolicies], isMounted.current)
      }
    }
    catch (error: any) {
      createBanner({ message: error.message })
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [createBanner, isMounted])

  useEffect(() => {
    loadPolicyData(tsg, selected)
  }, [loadPolicyData, selected, tsg])

  const onSubmit = useCallback(() => {
    _onSubmit(tsg, selected)
  }, [_onSubmit, selected, tsg])

  return (
    <>
      <ModalBody>
        {loading && (<ScreenLoader appearance='light'/>)}
        <div className='cm-flex cm-flex-col cm-gap-2'>
          <Body>{StringTemplate(LABEL_TENANT_WILL_BE_MOVED, [tsg.display_name, parent.display_name, selected.display_name])}</Body>
          {!!lostPolicies.length && (
            <Body>{LABEL_TENANT_MOVED_USERS_LOST}</Body>
          )}
          <Body>{LABEL_ARE_YOU_SURE}</Body>
          {!!lostPolicies.length && (
            <MoveTenantPreviewTable
              {...{ loading, policies: lostPolicies }}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={onBack}
          disabled={loading}
        >{BUTTON_BACK}</Button>
        <Button
          onClick={onSubmit}
          appearance='primary'
          disabled={loading}
        >{BUTTON_SUBMIT}</Button>
      </ModalFooter>
    </>
  )
}

export default MoveTenantPreviewContent
